import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Alert,
  Autocomplete,
  Chip,
  Button
} from '@mui/material';
import { useSkillSheetMainStepper } from '../context/SkillSheetMainStepperContext';

export default function MultiSelectForm({
  title,
  fieldName,
  masterDataKey,
  backendFieldName,
  stepIndex,
  placeholder,
  isEdit = false,
  showSaveButton = false
}) {
  // コンテキストの取得を条件分岐で行う
  let stepperContext;

  if (isEdit) {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('編集用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  } else {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('作成用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  }

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    fetchMasterData,
    masterData,
    validateStep,
    submitForm
  } = stepperContext;

  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);
  // 入力中のテキスト状態
  const [inputValue, setInputValue] = useState('');

  // フォームの値を監視
  const selectedItems = watch(fieldName) || [];

  // 既に選択済みのアイテムを取得する関数
  const getSelectedItems = useCallback(() => {
    return selectedItems.map(item => 
      typeof item === 'string' ? item : item.name || ''
    ).filter(name => name && name.trim() !== '');
  }, [selectedItems]);

  // アイテムの選択肢をフィルタリングする関数
  const getFilteredItemOptions = useCallback(() => {
    const selectedItemNames = getSelectedItems();

    return (masterData[masterDataKey] || []).filter(item => {
      const itemName = typeof item === 'string' ? item : item.name;
      return !selectedItemNames.includes(itemName);
    });
  }, [masterData, masterDataKey, getSelectedItems]);

  // マスタデータを取得
  useEffect(() => {
    fetchMasterData(masterDataKey);
  }, [fetchMasterData, masterDataKey]);

  // バリデーション関数
  const validateForm = () => {
    // これらのフィールドは任意項目なので、バリデーションエラーなし
    setShowValidation(false);
    return true;
  };

  // バリデーションエラーが表示された時の処理
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
    }
  }, [errors]);

  // フォームバリデーションを外部から呼び出せるように登録
  useEffect(() => {
    if (validateStep) {
      validateStep(stepIndex, validateForm);
    }
  }, [validateStep, selectedItems, stepIndex]);

  // 手動入力値を確定する関数
  const addManualInput = (inputText) => {
    if (inputText && inputText.trim() !== '') {
      const trimmedInput = inputText.trim();
      // 既に選択されているかチェック
      const isDuplicate = selectedItems.some(item =>
        (typeof item === 'string' ? item : item.name) === trimmedInput
      );

      if (!isDuplicate) {
        const newSelectedItems = [...selectedItems, trimmedInput];
        setValue(fieldName, newSelectedItems);

        // backend用のフォーマットに変換して保存
        if (backendFieldName) {
          const itemsForBackend = newSelectedItems.map(item => {
            const nameField = getNameField(backendFieldName);
            const idField = getIdField(backendFieldName);
            return {
              [nameField]: typeof item === 'string' ? item : item.name || '',
              [idField]: typeof item === 'string' ? '' : item.id || ''
            };
          });
          setValue(backendFieldName, itemsForBackend);
        }
      }

      // 入力フィールドをクリア
      setInputValue('');
    }
  };

  // backend用のフィールド名を生成する関数
  const getNameField = (backendFieldName) => {
    if (backendFieldName === 'operating_systems') return 'operating_system_name';
    if (backendFieldName === 'middlewares') return 'middleware_name';
    if (backendFieldName === 'dbs') return 'db_name';
    if (backendFieldName === 'networks') return 'network_name';
    if (backendFieldName === 'design_tools') return 'design_tool_name';
    if (backendFieldName === 'tools') return 'tool_name';
    return 'name';
  };

  const getIdField = (backendFieldName) => {
    if (backendFieldName === 'operating_systems') return 'operating_system_id';
    if (backendFieldName === 'middlewares') return 'middleware_id';
    if (backendFieldName === 'dbs') return 'db_id';
    if (backendFieldName === 'networks') return 'network_id';
    if (backendFieldName === 'design_tools') return 'design_tool_id';
    if (backendFieldName === 'tools') return 'tool_id';
    return 'id';
  };

  // アイテム選択の変更ハンドラ
  const handleItemsChange = (event, newValue, reason) => {
    setValue(fieldName, newValue);

    // backend用のフォーマットに変換して保存
    if (backendFieldName) {
      const itemsForBackend = newValue.map(item => {
        const nameField = getNameField(backendFieldName);
        const idField = getIdField(backendFieldName);
        return {
          [nameField]: typeof item === 'string' ? item : item.name || '',
          [idField]: typeof item === 'string' ? '' : item.id || ''
        };
      });
      setValue(backendFieldName, itemsForBackend);
    }

    // エラーをクリア
    if (errors[fieldName]) {
      clearErrors(fieldName);
    }
  };

  // 入力値変更ハンドラ
  const handleInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue);
  };

  // キーダウンイベントハンドラ（Enterキーで確定）
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      event.preventDefault();
      addManualInput(inputValue);
    }
  };

  // フォーカスアウト時のハンドラ
  const handleBlur = () => {
    if (inputValue.trim() !== '') {
      addManualInput(inputValue);
    }
  };

  const handleSave = async () => {
    const isValid = validateForm();
    if (isValid) {
      await submitForm();
    }
  };

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (showValidation && Object.keys(errors).length > 0) {
    Object.keys(errors).forEach(key => {
      if (key === fieldName) {
        validationErrors.push(errors[key].message);
      }
    });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ marginBottom: '24px' }}>
        {title}
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* アイテム選択 */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography sx={{ mb: 1 }}>{title}（複数選択可）</Typography>
            <Autocomplete
              multiple
              freeSolo
              options={getFilteredItemOptions()}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
              value={selectedItems}
              inputValue={inputValue}
              onChange={handleItemsChange}
              onInputChange={handleInputChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="filled"
                    label={typeof option === 'string' ? option : option.name}
                    {...getTagProps({ index })}
                    key={index}
                    sx={{
                      backgroundColor: '#4F46E5',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'white',
                        '&:hover': {
                          color: '#f0f0f0'
                        }
                      }
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  placeholder={placeholder}
                  variant="outlined"
                  error={!!errors[fieldName]}
                  helperText={errors[fieldName]?.message}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  sx={{
                    '& input:focus': {
                      boxShadow: 'none',
                    },
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-tag': {
                  margin: '2px',
                },
                '& .MuiAutocomplete-inputRoot': {
                  minHeight: '56px',
                  padding: '8px 12px',
                },
              }}
            />
          </Box>
        </Grid>

        {/* 保存ボタン（最後のステップのみ） */}
        {showSaveButton && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  bgcolor: '#4F46E5',
                  '&:hover': { bgcolor: '#4338CA' },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  minWidth: '200px'
                }}
              >
                保存
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
} 